import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Empty,
  Flex,
  Spin,
} from "antd";
import * as echarts from "echarts";
import mainDashboardApis from "../../api/dashboard/MainDashboard";
import { formatCurrency, formatCurrency2 } from "../../utils/Utils";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  Icon,
} from "../../components/Component";
import { Button } from "reactstrap";
import { useNavigate, useLocation } from "react-router-dom";
import PRSummaryCategoryDrawer from "./PRSummaryCategoryDrawer";
import { useSelector } from "react-redux";

const PRSummaryDetailsPage: React.FC = () => {
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [categoryData, setCategoryData] = useState<any[]>([]);
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);

  // Get date range from location state or use default
  const locationState = location.state as any;
  const startDate = locationState?.startDate || "";
  const endDate = locationState?.endDate || "";
  
  // Check if drawer should be opened when returning from PR view
  useEffect(() => {
    if (locationState?.openDrawer && locationState?.categoryId) {
      setSelectedCategoryId(String(locationState.categoryId));
      setDrawerVisible(true);
    }
  }, [location]);

  // Get siteId from currentUser
  const getSiteId = () => {
    if (currentUser?.siteAccessibility) {
      const allSites = currentUser.siteAccessibility.map((item: any) => item.siteID);
      return allSites.join(",");
    }
    return "";
  };
  const siteId = getSiteId();

  const fetchMaterialQtyOverview = useCallback(async () => {
    if (!startDate || !endDate) {
      console.error("Start date and end date are required");
      return;
    }
    setIsLoading(true);
    try {
      const res = await mainDashboardApis.prSummaryMaterialQtyOverview({
        startDate: startDate,
        endDate: endDate,
      });
      console.log("PR Summary Material Qty Overview response:", res);
      if (res?.status && res?.response?.data && res.response.data.length > 0) {
        setCategoryData(res.response.data);
      } else {
        setCategoryData([]);
      }
    } catch (error) {
      console.error("Error fetching PR Summary Material Qty Overview:", error);
      setCategoryData([]);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    console.log("PRSummaryDetailsPage mounted, calling API...");
    fetchMaterialQtyOverview();
  }, [fetchMaterialQtyOverview]);

  // Prepare data for chart from API response
  const prepareChartData = (data: any[]) => {
    const categories = data.map(item => item.categoryName);
    const values = data.map(item => parseFloat(item.materialQty) || 0);
    
    return {
      categories,
      values,
    };
  };

  const disposeChart = () => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.dispose();
      chartInstanceRef.current = null;
    }
  };

  const initializeChart = (data: any[]) => {
    if (chartContainerRef.current && data.length > 0) {
      if (!chartInstanceRef.current) {
        chartInstanceRef.current = echarts.init(chartContainerRef.current);
      }

      const chartData = prepareChartData(data);
      
      // Calculate max value and set Y-axis max to next 5000 interval
      const maxValue = Math.max(...chartData.values, 0);
      const yAxisMax = Math.ceil(maxValue / 5000) * 5000;
      const yAxisMin = 0;
      
      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          triggerOn: "mousemove|click",
          formatter: (params: any) => {
            const param = params[0];
            return `<div><p><strong>${param.name}</strong></p><p>Quantity: ${param.value.toLocaleString()}</p></div>`;
          },
        },
        grid: {
          left: "3%",
          right: "0",
          bottom: "12%",
          top: "5%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: chartData.categories,
          name: "Category",
          nameLocation: "middle",
          nameGap: 35,
          nameTextStyle: {
            fontWeight: "bold",
            fontSize: 14,
            padding: [8, 0, 0, 0],
          },
          axisLabel: {
            rotate: 45,
            interval: 0,
            fontSize: 12,
            margin: 8,
            align: "right",
            verticalAlign: "middle",
          },
          axisTick: {
            alignWithLabel: true,
          },
        },
        yAxis: {
          type: "value",
          name: `Material Quantity`,
          nameLocation: "middle",
          min: yAxisMin,
          max: yAxisMax,
          interval: 5000,
          axisLabel: {
            formatter: (value: number) => value.toLocaleString(),
          },
          nameTextStyle: {
            fontWeight: "bold",
            fontSize: 14,
            padding: 50,
          },
        },
        series: [
          {
            name: "Material Quantity",
            type: "bar",
            data: chartData.values,
            itemStyle: {
              color: "#5470C6",
            },
            barWidth: "60%",
            barCategoryGap: "20%",
          },
        ],
      };

      chartInstanceRef.current.setOption(option);

      // Add click event handler to open drawer with category details
      chartInstanceRef.current.off('click');
      chartInstanceRef.current.on('click', (params: any) => {
        const clickedData = data[params.dataIndex];
        if (clickedData?.categoryId) {
          setSelectedCategoryId(String(clickedData.categoryId));
          setDrawerVisible(true);
        }
      });

      // Trigger click anywhere in the bar's column area (not just on the bar)
      const zr = chartInstanceRef.current.getZr();
      zr.off('click');
      zr.on('click', (event: any) => {
        if (!chartInstanceRef.current) return;
        const pointInPixel = [event.offsetX, event.offsetY];
        if (chartInstanceRef.current.containPixel('grid', pointInPixel)) {
          const xIndex = chartInstanceRef.current.convertFromPixel(
            { seriesIndex: 0 },
            pointInPixel
          )[0];
          const clickedData = data[xIndex];
          if (clickedData?.categoryId) {
            setSelectedCategoryId(String(clickedData.categoryId));
            setDrawerVisible(true);
          }
        }
      });
    }
  };

  useEffect(() => {
    if (categoryData.length > 0) {
      initializeChart(categoryData);
    } else {
      disposeChart();
    }
    
    return () => {
      disposeChart();
    };
  }, [categoryData]);

  return (
    <React.Fragment>
      <Head title="Purchase Request Summary - Category Wise Details" />
      <Content>
        <BlockHead size="lg">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h4">
                <span>Purchase Request Summary - Category Wise Details</span>
              </BlockTitle>
            </BlockHeadContent>
            <div className="actions clearfix">
              <ul className="m-auto d-flex align-items-center justify-content-center p-0">
                <li className="pt-0 pb-0">
                  <Button
                    className="toggle"
                    color="primary"
                    onClick={() => {
                      navigate("/dashboard");
                    }}
                  >
                    <Icon name="arrow-left" />
                    <span>Back</span>
                  </Button>
                </li>
              </ul>
            </div>
          </BlockBetween>
        </BlockHead>
        <Block size="lg">
          {isLoading ? (
            <Flex align="center" gap="middle" className="pageLoader">
              <Spin size="large" className="m-auto" />
            </Flex>
          ) : categoryData.length === 0 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <Empty description="No Data Available" />
            </div>
          ) : (
            <div>
              {/* Chart with All Categories */}
              <div
                style={{
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  style={{
                    marginBottom: "20px",
                  }}
                >
                  <h4 style={{ margin: 0, color: "#333" }}>
                    Material Quantity by Category
                  </h4>
                </div>
                {categoryData.length > 0 ? (
                  <div
                    ref={chartContainerRef}
                    style={{ width: "100%", height: "calc(100vh - 300px)", minHeight: "700px" }}
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "calc(100vh - 300px)",
                      minHeight: "700px",
                    }}
                  >
                    <Empty description="No data available to generate the graph." />
                  </div>
                )}
              </div>
            </div>
          )}
        </Block>
      </Content>
      {drawerVisible && (
        <PRSummaryCategoryDrawer
          visible={drawerVisible}
          onClose={() => {
            setDrawerVisible(false);
            setSelectedCategoryId("");
          }}
          categoryId={selectedCategoryId}
          siteId={siteId}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </React.Fragment>
  );
};

export default PRSummaryDetailsPage;

